import { db } from './firebase';
import {
  collection, collectionGroup, doc, getDoc, getDocs, addDoc, setDoc, updateDoc, deleteDoc, query, where, orderBy, limit, serverTimestamp, onSnapshot,
} from 'firebase/firestore';

const COL = 'youtube_assignments';

// 팀방의 멤버 mcode 배열을 가져옴 (uid = mcode 로 사용)
async function getTeamMemberMcodes(roomId) {
  if (!roomId) return [];
  try {
    const snap = await getDoc(doc(db, 'chatRooms', roomId));
    if (!snap.exists()) return [];
    const data = snap.data();
    return Array.isArray(data.members) ? data.members.map(String) : [];
  } catch (e) {
    console.warn('팀 멤버 조회 실패:', e);
    return [];
  }
}

// 팀방 멤버 중 팀장(members.dept === '팀장')의 mcode 배열만 추출
async function getTeamLeaderMcodes(roomId) {
  const mcodes = await getTeamMemberMcodes(roomId);
  if (mcodes.length === 0) return [];
  const out = [];
  await Promise.all(mcodes.map(async (mc) => {
    try {
      const m = await getDoc(doc(db, 'members', mc));
      if (!m.exists()) return;
      const d = m.data();
      if (d.dept === '팀장') out.push(String(mc));
    } catch {}
  }));
  return out;
}

// notifications 컬렉션에 푸시 발송 요청 — 1분 스케줄러가 처리(functions/index.js)
async function enqueueNotification({ type, title, body, targetUids, data }) {
  const uids = Array.from(new Set((targetUids || []).filter(Boolean).map(String)));
  if (uids.length === 0) return;
  const finalData = { ...(data || {}) };
  // 딥링크: 토스 알림 터치 → 유튜브방 열고 해당 토스 카드로 스크롤 (assignmentId 기준)
  if (!finalData.link && finalData.assignmentId) {
    finalData.link = `/?menu=teamRoom&toss=${finalData.assignmentId}`;
  }
  await addDoc(collection(db, 'notifications'), {
    type: type || 'youtube_toss',
    title: title || '',
    body: body || '',
    targetUids: uids,
    data: finalData,
    sent: false,
    createdAt: serverTimestamp(),
  });
}

// 팀 배정 푸시 — 해당 팀의 팀장에게만 (배정 발송자 본인은 제외)
// 팀장이 식별되지 않으면 안전망으로 팀 전원에게 발송 (알림 누락 방지)
export async function notifyYoutubeTossAssign({ roomId, advertiserName, teamLabel, articleTitle, articleAddr, customerPhone, meetingDate, meetingTime, aidx, assignmentId, isReassign, senderMcode }) {
  let candidates = await getTeamLeaderMcodes(roomId);
  if (candidates.length === 0) {
    candidates = await getTeamMemberMcodes(roomId);
  }
  const targets = candidates.filter((m) => String(m) !== String(senderMcode || ''));
  if (targets.length === 0) return;

  const titlePrefix = isReassign ? '[유튜브토스 · 재배정]' : '[유튜브토스]';
  const title = `${titlePrefix} ${advertiserName || '유튜버'} → ${teamLabel || '팀'}`;
  const articleText = articleTitle || articleAddr || '매물 미정';
  const meetText = [meetingDate, meetingTime].filter(Boolean).join(' ');
  const bodyParts = [`매물: ${articleText}`];
  if (customerPhone) bodyParts.push(`손님: ${customerPhone}`);
  if (meetText) bodyParts.push(`미팅: ${meetText}`);
  const body = bodyParts.join(' · ');

  await enqueueNotification({
    type: 'youtube_toss_assign',
    title: body ? `${title} · ${body}` : title,
    body: '',
    targetUids: targets,
    data: { aidx: aidx || '', assignmentId: assignmentId || '', roomId: roomId || '' },
  });
}

// 담당 직원 배정 푸시 — 팀장이 팀원을 배정했을 때, 배정받은 본인에게만 발송
export async function notifyYoutubeTossMemberAssigned({ assignmentId, assigneeMcode, assignedByName, senderMcode }) {
  if (!assignmentId || !assigneeMcode) return;
  if (String(assigneeMcode) === String(senderMcode || '')) return;
  try {
    const snap = await getDoc(doc(db, COL, assignmentId));
    if (!snap.exists()) return;
    const a = snap.data();

    const articleText = a.articleTitle || a.articleAddr || '매물 미정';
    const meetText = [a.meetingDate, a.meetingTime].filter(Boolean).join(' ');
    const title = `[유튜브토스 · 담당배정] ${a.teamLabel || '팀'}`;
    const bodyParts = [`매물: ${articleText}`];
    if (a.customerPhone) bodyParts.push(`손님: ${a.customerPhone}`);
    if (meetText) bodyParts.push(`미팅: ${meetText}`);
    if (assignedByName) bodyParts.push(`배정: ${assignedByName}`);
    const body = bodyParts.join(' · ');

    // 직원 본인 + 유튜버(토스 보낸 사람)에게 발송 — 유튜버가 '내 토스가 직원에게 배정 완료됨'을 알도록.
    // 배정된 팀의 팀장도 함께 받는다 — 팀장이 직접 배정하지 않은 경우(관리자/다른 팀장이 배정)에도
    // 자기 팀원이 누구를 맡았는지 놓치지 않게. (2026-07-30 김재용 제보)
    const advertiserMcode = a.advertiserMcode ? String(a.advertiserMcode) : '';
    const memberTargets = [String(assigneeMcode)];
    if (advertiserMcode) memberTargets.push(advertiserMcode);
    if (a.roomId) {
      const leaders = await getTeamLeaderMcodes(a.roomId);
      leaders.forEach((mc) => memberTargets.push(String(mc)));
    }
    const memberFiltered = memberTargets.filter((m) => m !== String(senderMcode || ''));

    await enqueueNotification({
      type: 'youtube_toss_member_assigned',
      title: body ? `${title} · ${body}` : title,
      body: '',
      targetUids: memberFiltered,
      data: { aidx: a.aidx || '', assignmentId, roomId: a.roomId || '' },
    });
  } catch (e) {
    console.warn('담당자 배정 푸시 발송 실패:', e);
  }
}

// 팀장확인 마킹 — 유튜브방에서 팀장이 토스 카드 클릭(읽음)
export async function markLeaderConfirmed(assignmentId, { leaderMcode, leaderName }) {
  if (!assignmentId) return;
  await updateDoc(doc(db, COL, assignmentId), {
    leaderConfirmedAt: serverTimestamp(),
    leaderConfirmedBy: leaderName || '',
    leaderConfirmedMcode: leaderMcode || '',
  });
}

// 팀원확인 마킹 — 미팅방에서 팀원이 토스 카드 클릭(읽음)
export async function markMemberConfirmed(assignmentId, { memberMcode, memberName }) {
  if (!assignmentId) return;
  await updateDoc(doc(db, COL, assignmentId), {
    memberConfirmedAt: serverTimestamp(),
    memberConfirmedBy: memberName || '',
    memberConfirmedMcode: memberMcode || '',
  });
}

// 팀장확인 푸시 — 유튜브방에서 팀장이 클릭한 시점, 유튜버에게만
export async function notifyYoutubeTossLeaderConfirmed({ assignmentId, leaderName, senderMcode }) {
  if (!assignmentId) return;
  try {
    const snap = await getDoc(doc(db, COL, assignmentId));
    if (!snap.exists()) return;
    const a = snap.data();
    const advertiserMcode = a.advertiserMcode ? String(a.advertiserMcode) : '';
    if (!advertiserMcode || advertiserMcode === String(senderMcode || '')) return;

    const articleText = a.articleTitle || a.articleAddr || '매물 미정';
    const meetText = [a.meetingDate, a.meetingTime].filter(Boolean).join(' ');
    const title = `[유튜브토스 · 팀장확인] ${a.teamLabel || '팀'}`;
    const bodyParts = [`매물: ${articleText}`];
    if (a.customerPhone) bodyParts.push(`손님: ${a.customerPhone}`);
    if (meetText) bodyParts.push(`미팅: ${meetText}`);
    if (leaderName) bodyParts.push(`확인: ${leaderName}`);
    const body = bodyParts.join(' · ');

    await enqueueNotification({
      type: 'youtube_toss_leader_confirmed',
      title: body ? `${title} · ${body}` : title,
      body: '',
      targetUids: [advertiserMcode],
      data: { aidx: a.aidx || '', assignmentId, roomId: a.roomId || '' },
    });
  } catch (e) {
    console.warn('팀장확인 푸시 발송 실패:', e);
  }
}

// 팀원확인 푸시 — 미팅방에서 배정받은 팀원이 클릭한 시점, 유튜버 + 팀장에게
export async function notifyYoutubeTossMemberConfirmed({ assignmentId, memberName, senderMcode }) {
  if (!assignmentId) return;
  try {
    const snap = await getDoc(doc(db, COL, assignmentId));
    if (!snap.exists()) return;
    const a = snap.data();

    let leaders = await getTeamLeaderMcodes(a.roomId);
    const advertiserMcode = a.advertiserMcode ? String(a.advertiserMcode) : '';
    const targets = [...leaders];
    if (advertiserMcode) targets.push(advertiserMcode);
    const filtered = targets.filter((m) => String(m) !== String(senderMcode || ''));
    if (filtered.length === 0) return;

    const articleText = a.articleTitle || a.articleAddr || '매물 미정';
    const meetText = [a.meetingDate, a.meetingTime].filter(Boolean).join(' ');
    const title = `[유튜브토스 · 팀원확인] ${a.teamLabel || '팀'}`;
    const bodyParts = [`매물: ${articleText}`];
    if (a.customerPhone) bodyParts.push(`손님: ${a.customerPhone}`);
    if (meetText) bodyParts.push(`미팅: ${meetText}`);
    if (memberName) bodyParts.push(`담당: ${memberName}`);
    const body = bodyParts.join(' · ');

    await enqueueNotification({
      type: 'youtube_toss_member_confirmed',
      title: body ? `${title} · ${body}` : title,
      body: '',
      targetUids: filtered,
      data: { aidx: a.aidx || '', assignmentId, roomId: a.roomId || '' },
    });
  } catch (e) {
    console.warn('팀원확인 푸시 발송 실패:', e);
  }
}

// 다른팀배정 요청 — 팀장이 자기 팀에 배정 가능한 팀원이 없을 때 유튜버에게 반환 요청
export async function requestReassign(assignmentId, { leaderMcode, leaderName }) {
  if (!assignmentId) return;
  await updateDoc(doc(db, COL, assignmentId), {
    reassignRequestedAt: serverTimestamp(),
    reassignRequestedBy: leaderName || '',
    reassignRequestedMcode: leaderMcode || '',
  });
}

// 다른팀배정 요청 푸시 — 유튜버(토스 보낸 사람)에게만
export async function notifyYoutubeTossReassignRequest({ assignmentId, leaderName, senderMcode }) {
  if (!assignmentId) return;
  try {
    const snap = await getDoc(doc(db, COL, assignmentId));
    if (!snap.exists()) return;
    const a = snap.data();
    const advertiserMcode = a.advertiserMcode ? String(a.advertiserMcode) : '';
    if (!advertiserMcode || advertiserMcode === String(senderMcode || '')) return;

    const articleText = a.articleTitle || a.articleAddr || '매물 미정';
    const meetText = [a.meetingDate, a.meetingTime].filter(Boolean).join(' ');
    const title = `[유튜브토스 · 다른팀배정요청] ${a.teamLabel || '팀'}`;
    const bodyParts = [`매물: ${articleText}`];
    if (a.customerPhone) bodyParts.push(`손님: ${a.customerPhone}`);
    if (meetText) bodyParts.push(`미팅: ${meetText}`);
    if (leaderName) bodyParts.push(`요청: ${leaderName}`);
    const body = bodyParts.join(' · ');

    await enqueueNotification({
      type: 'youtube_toss_reassign_request',
      title: body ? `${title} · ${body}` : title,
      body: '',
      targetUids: [advertiserMcode],
      data: { aidx: a.aidx || '', assignmentId, roomId: a.roomId || '' },
    });
  } catch (e) {
    console.warn('다른팀배정요청 푸시 발송 실패:', e);
  }
}

// 미팅 결과 푸시 — 우리 팀 팀장 + 해당 유튜버 (입력자 본인은 제외)
// 팀장이 식별되지 않으면 안전망으로 팀 전원에게 발송
export async function notifyYoutubeTossResult({ assignmentId, resultText, resultBy, senderMcode }) {
  if (!assignmentId) return;
  try {
    const snap = await getDoc(doc(db, COL, assignmentId));
    if (!snap.exists()) return;
    const a = snap.data();
    let teamTargets = await getTeamLeaderMcodes(a.roomId);
    if (teamTargets.length === 0) {
      teamTargets = await getTeamMemberMcodes(a.roomId);
    }
    const advertiserMcode = a.advertiserMcode ? String(a.advertiserMcode) : '';
    const targets = [...teamTargets];
    if (advertiserMcode) targets.push(advertiserMcode);
    const filtered = targets.filter((m) => String(m) !== String(senderMcode || ''));
    if (filtered.length === 0) return;

    const articleText = a.articleTitle || a.articleAddr || '매물 미정';
    const title = `[미팅결과] ${a.teamLabel || '팀'} · ${resultBy || ''}${a.customerPhone ? ` · 손님 ${a.customerPhone}` : ''}`.trim();
    const bodyText = (resultText || '').slice(0, 180);
    const body = `${articleText}\n${bodyText}`;

    await enqueueNotification({
      type: 'youtube_toss_result',
      title,
      body,
      targetUids: filtered,
      data: { aidx: a.aidx || '', assignmentId, roomId: a.roomId || '' },
    });
  } catch (e) {
    console.warn('미팅 결과 푸시 발송 실패:', e);
  }
}

// 단건 저장 — 배정 기록 + articles 캐시 갱신은 호출측에서 묶어 처리
export async function addYoutubeAssignment(data) {
  const ref = await addDoc(collection(db, COL), {
    ...data,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

// 잘못 등록된 배정 삭제 — 본인 또는 관리자만 호출하도록 호출측에서 권한 검사
export async function deleteAssignment(assignmentId) {
  if (!assignmentId) return;
  await deleteDoc(doc(db, COL, assignmentId));
}

// 유튜버가 보낸 토스를 취소 — 완전 삭제 대신 canceled 플래그를 남겨,
// 받는 팀 화면에 "○○○ 유튜버가 취소한 매물입니다"로 표시되도록 한다.
export async function cancelAssignment(assignmentId, { byMcode, byName } = {}) {
  if (!assignmentId) return;
  await updateDoc(doc(db, COL, assignmentId), {
    canceled: true,
    canceledByMcode: byMcode || '',
    canceledByName: byName || '',
    canceledAt: serverTimestamp(),
  });
}

// 미팅 결과 업데이트 (팀이 미팅 다녀온 후 결과 기록)
export async function updateAssignmentResult(assignmentId, resultText, resultBy) {
  if (!assignmentId) return;
  await updateDoc(doc(db, COL, assignmentId), {
    result: resultText || '',
    resultBy: resultBy || '',
    resultAt: serverTimestamp(),
  });
}

// 토스 손님/미팅 정보 수정 (잘못 기재된 손님 연락처·미팅일시 등 정정 — 2026-06-26 #10 박상진)
// 손님정보 관련 필드만 갱신 (팀배정/결과 등은 건드리지 않음)
export async function updateYoutubeAssignment(assignmentId, fields = {}) {
  if (!assignmentId) return;
  const allow = ['callDate', 'callTime', 'meetingDate', 'meetingTime', 'adPrice', 'customerPhone', 'actualDeposit', 'loanAmount', 'moveDate', 'saengStatus', 'salesStatus', 'falseContent', 'customerNote', 'memo'];
  const patch = { updatedAt: serverTimestamp() };
  allow.forEach((k) => { if (fields[k] !== undefined) patch[k] = fields[k]; });
  await updateDoc(doc(db, COL, assignmentId), patch);
}

// 토스 손님정보 수정 시, 이미 발행된 팀방 카드 메시지(chatRooms/{roomId}/messages, type='meeting')도
// 함께 동기화 — 방 카드는 최초 토스 시점의 스냅샷(msgText/customerPhone 등)을 그대로 읽어서
// 정정 내용이 반영되지 않던 문제 수정 (2026-07-19 박상진 제보)
export async function syncTossCardMessage(assignmentId, { roomId, patch } = {}) {
  if (!assignmentId || !patch) return 0;
  try {
    let docs = [];
    if (roomId) {
      const snap = await getDocs(query(
        collection(db, 'chatRooms', roomId, 'messages'), where('assignmentId', '==', assignmentId),
      ));
      docs = snap.docs;
    }
    if (docs.length === 0) {
      // roomId 미상 또는 못 찾음 → 전체 messages 컬렉션그룹에서 탐색
      const snap = await getDocs(query(
        collectionGroup(db, 'messages'), where('assignmentId', '==', assignmentId),
      ));
      docs = snap.docs;
    }
    const body = { ...patch, updatedAt: serverTimestamp() };
    await Promise.all(docs.map((d) => updateDoc(d.ref, body)));
    return docs.length;
  } catch (e) {
    console.warn('토스 카드 메시지 동기화 실패:', e);
    return 0;
  }
}

// 팀 멤버(이름 포함) 조회 — 콤보박스용
// 채팅방 members 배열에 잘못 들어있을 수 있어서, members 컬렉션의 store_idx/team_no가
// 채팅방의 storeIdx/teamNo와 일치하는 사람만 통과시킴. 퇴사자도 제외.
// 이름은 members.bang_name → logins.l_name 순으로 폴백 (members에 이름 없는 케이스 다수)
export async function fetchTeamMembersWithNames(roomId) {
  if (!roomId) return [];
  try {
    const roomSnap = await getDoc(doc(db, 'chatRooms', roomId));
    if (!roomSnap.exists()) return [];
    const room = roomSnap.data();
    const mcodes = Array.from(new Set((room.members || []).map(String).filter(Boolean)));
    if (mcodes.length === 0) return [];

    const targetStore = String(room.storeIdx || '');
    const targetTeam = String(room.teamNo || '');

    // logins 한 번에 로드: 활성 mcode 셋 + l_name 매핑
    const loginsSnap = await getDocs(collection(db, 'logins'));
    const activeMcodes = new Set();
    const nameFromLogin = {};
    loginsSnap.docs.forEach(l => {
      const d = l.data();
      const mc = String(d.l_code || l.id);
      if (d.l_delete !== 'Y' && d.l_enable !== 'N') activeMcodes.add(mc);
      if (d.l_name && !nameFromLogin[mc]) nameFromLogin[mc] = d.l_name;
    });

    const out = await Promise.all(mcodes.map(async (mcode) => {
      try {
        const m = await getDoc(doc(db, 'members', mcode));
        if (!m.exists()) return null;
        const d = m.data();
        if (String(d.store_idx || '') !== targetStore) return null;
        if (String(d.team_no || '') !== targetTeam) return null;
        if (mcode !== 'A0000001' && !activeMcodes.has(mcode)) return null;
        const name = d.bang_name || d.name || nameFromLogin[mcode] || '';
        return { mcode, name, dept: d.dept || '' };
      } catch {
        return null;
      }
    }));
    return out.filter(x => x && x.name);
  } catch (e) {
    console.warn('팀 멤버 조회 실패:', e);
    return [];
  }
}

// 우리 팀에 들어온 토스 구독 — 팀장 view 미확인 카운트용
export function subscribeTeamAssignments(teamId, callback) {
  if (!teamId) { callback([]); return () => {}; }
  const q = query(collection(db, COL), where('teamId', '==', String(teamId)));
  return onSnapshot(q, (snap) => {
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(list);
  }, (e) => { console.warn('팀 토스 구독 실패:', e); callback([]); });
}

// 본인이 배정받은 미팅 구독 — 메인 미팅예정 카드 노출용 (assigneeMcode 일치)
export function subscribeMyAssignments(myMcode, callback) {
  if (!myMcode) { callback([]); return () => {}; }
  const q = query(collection(db, COL), where('assigneeMcode', '==', String(myMcode)));
  return onSnapshot(q, (snap) => {
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(list);
  }, (e) => { console.warn('내 배정 구독 실패:', e); callback([]); });
}

// 미팅 담당자(팀원) 배정/변경
export async function updateAssignmentMember(assignmentId, { assigneeMcode, assigneeName, assignedBy }) {
  if (!assignmentId) return;
  await updateDoc(doc(db, COL, assignmentId), {
    assigneeMcode: assigneeMcode || '',
    assigneeName: assigneeName || '',
    assignedAt: serverTimestamp(),
    assignedBy: assignedBy || '',
  });
}

// 매물 한 건의 토스 이력 (시간 역순) — 복합 인덱스 회피 위해 클라이언트 정렬
// 매물 미정 토스는 aidx 를 비워 저장하므로, 토스를 시작한 매물(originAidx)로도 함께 찾는다.
// 이게 없으면 미정으로 보낸 손님이 그 매물의 토스 창에 안 남아 이력 조회·손님정보 수정을 못 한다 (2026-10-02 제보)
export async function fetchAssignmentsByArticle(aidx, max = 50) {
  if (!aidx) return [];
  const [snap, originSnap] = await Promise.all([
    getDocs(query(collection(db, COL), where('aidx', '==', String(aidx)))),
    getDocs(query(collection(db, COL), where('originAidx', '==', String(aidx)))),
  ]);
  const seen = new Set();
  const docs = [];
  [...snap.docs, ...originSnap.docs].forEach(d => {
    if (seen.has(d.id)) return;
    seen.add(d.id);
    docs.push({ id: d.id, ...d.data() });
  });
  docs.sort((a, b) => {
    const ta = a.createdAt?.seconds || 0;
    const tb = b.createdAt?.seconds || 0;
    return tb - ta;
  });
  return docs.slice(0, max);
}

// 전체 최근 토스 이력 — 균형 판단용
export async function fetchRecentAssignments(max = 30) {
  const q = query(
    collection(db, COL),
    orderBy('createdAt', 'desc'),
    limit(max)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// 토스 이력 화면에서만 숨김 처리 (실제 assignment 데이터는 유지)
export async function hideAssignmentFromHistory(assignmentId) {
  await updateDoc(doc(db, COL, assignmentId), {
    hiddenInHistory: true,
    hiddenAt: serverTimestamp(),
  });
}

// 전체 최근 토스 이력 실시간 구독 — 토스 이력 화면용
export function subscribeRecentAssignments(callback, max = 500) {
  const q = query(
    collection(db, COL),
    orderBy('createdAt', 'desc'),
    limit(max)
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

// 같은 손님 + 같은 매물 토스 중복 검사
export async function fetchAssignmentsByCustomerArticle(customerPhone, aidx) {
  if (!customerPhone || !aidx) return [];
  const q = query(
    collection(db, COL),
    where('aidx', '==', String(aidx)),
    where('customerPhone', '==', String(customerPhone))
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// 팀별 누적 카운트 (전체 기간) — 균형 표시용
export async function fetchTeamCounts() {
  const snap = await getDocs(collection(db, COL));
  const counts = {};
  snap.docs.forEach(d => {
    const x = d.data();
    const key = x.teamId || `${x.storeIdx}_${x.teamNo}`;
    if (!key) return;
    if (!counts[key]) counts[key] = { teamId: key, teamLabel: x.teamLabel || '', count: 0 };
    counts[key].count++;
    if (!counts[key].teamLabel && x.teamLabel) counts[key].teamLabel = x.teamLabel;
  });
  return Object.values(counts).sort((a, b) => b.count - a.count);
}

// 매물에 마지막 배정 정보 캐시
export async function updateArticleLastToss(aidx, lastTossTeamId, lastTossTeamLabel) {
  if (!aidx) return;
  await setDoc(doc(db, 'articles', String(aidx)), {
    lastTossTeamId,
    lastTossTeamLabel,
    lastTossAt: serverTimestamp(),
  }, { merge: true });
}

// 메모 템플릿 — 유튜버(작성자) 본인 mcode 단위로 저장
const MEMO_TPL_COL = 'youtube_toss_memo_templates';

export async function fetchMemoTemplates(ownerMcode) {
  if (!ownerMcode) return [];
  const q = query(collection(db, MEMO_TPL_COL), where('ownerMcode', '==', String(ownerMcode)));
  const snap = await getDocs(q);
  const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  list.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
  return list;
}

export async function addMemoTemplate({ ownerMcode, label, content }) {
  if (!ownerMcode) throw new Error('mcode 없음');
  const ref = await addDoc(collection(db, MEMO_TPL_COL), {
    ownerMcode: String(ownerMcode),
    label: label || '',
    content: content || '',
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function deleteMemoTemplate(id) {
  if (!id) return;
  await deleteDoc(doc(db, MEMO_TPL_COL, id));
}
